import type { GolfRound } from "@booking/booking/api/booking/types";
import {
  allocatedRoundsCountForCourse,
  isCourseUnallocated,
  isCourseUnlimited,
  roundsOnOffer,
} from "@booking/booking/api/offer/utils";
import type { SelectedTeeSlot } from "@booking/booking/api/teeTimes/types";
import { filterEntries } from "@booking/utils/helpers";
import { isEqual as isDateEqual, startOfDay } from "date-fns";
import { type StateCreator } from "zustand";

import { type Rounds, type RoundsState } from "./types";

const anyRoundsBeforeIf = (
  rounds: Rounds,
  roundId: number,
  condition: (teeSlot: SelectedTeeSlot) => boolean,
) => Object.keys(roundsBeforeIf(rounds, roundId, condition)).length > 0;

const roundsBeforeIf = (
  rounds: Rounds,
  roundId: number,
  condition: (teeSlot: SelectedTeeSlot) => boolean,
) =>
  filterEntries(
    rounds,
    ([round, teeSlots]) =>
      +round < roundId &&
      teeSlots.some((slot: SelectedTeeSlot) => condition(slot)),
  );

export const createRoundsState: StateCreator<RoundsState> = (set, get) => ({
  rounds: {},
  autoTimesFlow: false,
  leadGolferFirstName: "",
  leadGolferLastName: "",

  roundsActions: {
    preselectGolfRounds: (golfRounds: GolfRound[]) => {
      const rounds = golfRounds.reduce<{ [key: number]: SelectedTeeSlot[] }>(
        (acc, { courseId, teeTimes }, index) => {
          const roundId = index + 1;
          const selectedTeeSlots = teeTimes.map(
            ({ ttaId, teeTimeAt, noPlayers, sourceType }): SelectedTeeSlot => ({
              teeId: ttaId,
              time: new Date(teeTimeAt),
              timeslot: teeTimeAt,
              selectedTees: noPlayers,
              courseId,
              sourceType,
            }),
          );

          acc[roundId] = selectedTeeSlots;
          return acc;
        },
        {},
      );

      set({ rounds });
    },

    secureAutoTimes: () => set({ autoTimesFlow: true }),

    endAutoTimes: () => set({ autoTimesFlow: false }),

    setLeadGolferFirstName: (firstName: string) =>
      set({ leadGolferFirstName: firstName }),

    setLeadGolferLastName: (lastName: string) =>
      set({ leadGolferLastName: lastName }),

    addRound: (roundId: number, round: SelectedTeeSlot[]) =>
      set((state) => ({ rounds: { ...state.rounds, [roundId]: round } })),

    removeAllRounds: () => set({ rounds: {} }),

    removeRound: (roundId: number) => {
      const rounds = { ...get().rounds };

      delete rounds[roundId];
      set({ rounds });
    },

    removeSlots: (teeIds: string[]): string => {
      const rounds = { ...get().rounds };
      const affectedRounds: number[] = [];

      Object.entries(rounds).forEach(([roundId, slots]) => {
        rounds[+roundId] = slots.filter((slot) => !teeIds.includes(slot.teeId));
        if (rounds[+roundId].length !== slots.length) {
          affectedRounds.push(+roundId);
        }
        if (rounds[+roundId].length === 0) {
          delete rounds[+roundId];
        }
      });

      set({ rounds });
      return affectedRounds.length === 0
        ? "1"
        : Math.min(...affectedRounds).toString();
    },

    syncSelectedSlots: (teeIds: string[]): void => {
      const rounds = { ...get().rounds };
      const teeIdsSet = new Set(teeIds);

      Object.entries(rounds).forEach(([roundId, slots]) => {
        rounds[+roundId] = slots.filter(({ teeId }) => teeIdsSet.has(teeId));
        if (rounds[+roundId].length === 0) {
          delete rounds[+roundId];
        }
      });

      set({ rounds });
    },
  },

  roundsUtils: {
    allRoundsSelected: (offer) =>
      !!offer && Object.keys(get().rounds).length === roundsOnOffer(offer),

    isDateSelectedBefore: (roundId, date) =>
      anyRoundsBeforeIf(get().rounds, roundId, (slot) =>
        isDateEqual(date, startOfDay(slot.time)),
      ),

    selectedCourses: () => {
      const savedRounds = get().rounds;
      const courseIds = Object.values(savedRounds).flatMap((teeTimes) => {
        return teeTimes.map((teeSlot: SelectedTeeSlot) => teeSlot.courseId);
      });
      return Array.from(new Set(courseIds));
    },

    shouldDisableCourseFor: (roundId, course, offer) => {
      // Course is one of unlimited rounds (never disabled)
      if (isCourseUnlimited(course, offer)) {
        return false;
      }

      // Course is one of unallocated rounds
      if (isCourseUnallocated(course, offer)) {
        const maxRoundsCount = offer.roundAllocation.unallocated?.rounds || 0;
        const unallocatedCourses =
          offer.roundAllocation.unallocated?.courses || [];
        const roundsForUnallocatedCourses = roundsBeforeIf(
          get().rounds,
          roundId,
          (slot) =>
            unallocatedCourses.some((course) => slot.courseId === course.id),
        );
        const roundsCount = Object.keys(roundsForUnallocatedCourses).length;

        return roundsCount >= maxRoundsCount;
      }

      // Course is one of allocated rounds
      const maxRoundsCount = allocatedRoundsCountForCourse(course, offer);
      const roundsForCourse = roundsBeforeIf(
        get().rounds,
        roundId,
        (slot) => slot.courseId === course.id,
      );
      const roundsCount = Object.keys(roundsForCourse).length;

      return roundsCount >= maxRoundsCount;
    },
  },
});
